import { v4 as uuidv4 } from "uuid";

import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";

export interface CampaignBatchOption {
  batchId: string;
  name: string;
}

const UNTITLED_BATCH = "Untitled batch";

/** Reads a campaign's batches from operations.batches for lists, labels and new entries. */
export class CampaignBatches {
  /** Batches in natural name order, so "Batch 2" comes before "Batch 10". */
  public static list(campaign: InfluencerCampaign): CampaignBatchOption[] {
    return Object.entries(campaign.operations?.batches ?? {})
      .flatMap(([batchId, batch]) => (batch ? [{ batchId, name: batch.name?.trim() || UNTITLED_BATCH }] : []))
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true }));
  }

  /** Undefined for no batch and for a batch that no longer exists. */
  public static nameOf(campaign: InfluencerCampaign, batchId: string | undefined): string | undefined {
    if (!batchId) return undefined;
    return CampaignBatches.list(campaign).find((batch) => batch.batchId === batchId)?.name;
  }

  /** One past the highest "Batch N", so a deleted middle batch never yields a duplicate name. */
  public static nextName(campaign: InfluencerCampaign): string {
    const numbers = CampaignBatches.list(campaign).map((batch) => Number(/^Batch (\d+)$/.exec(batch.name)?.[1] ?? 0));
    return `Batch ${Math.max(0, ...numbers) + 1}`;
  }

  // A UUID fits the server's 36-character url-safe batch key; uuid works on plain HTTP hosts, unlike crypto.randomUUID.
  public static newBatchId(): string {
    return uuidv4();
  }
}
